import { z } from "zod";
import {
	AdminListInputSchema,
	adminItemOutput,
	adminListOutput,
} from "./common";

// =============
// Admin Referral Link Schemas
// =============

/**
 * Admin referral link schema - includes deletedAt for soft delete visibility.
 */
export const AdminReferralLinkSchema = z.object({
	id: z.string(),
	appId: z.string(),
	appName: z.string().nullish(), // Joined from apps table
	partnerId: z.string(),
	partnerName: z.string().nullish(), // Joined from partners table
	slug: z.string(),
	payoutRateBps: z.number().int().nonnegative(),
	minPayoutCents: z.number().int().nonnegative(),
	payoutDurationDays: z.number().int().positive().nullable(),
	planHint: z.enum(["monthly", "yearly"]).nullable(),
	maxRedemptions: z.number().int().positive().nullable(),
	redemptionCount: z.number().int().nonnegative().default(0),
	expiresAt: z.coerce.date().nullable(),
	isActive: z.boolean(),
	createdAt: z.coerce.date(),
	deletedAt: z.coerce.date().nullable(),
	/** Partner approval timestamp - null if not yet approved, immutable once set */
	partnerAgreedAt: z.coerce.date().nullable(),
});
export type AdminReferralLink = z.infer<typeof AdminReferralLinkSchema>;

/**
 * Admin referral link list input with link-specific filters.
 */
export const AdminReferralLinkListInputSchema = AdminListInputSchema.extend({
	filter: z
		.object({
			appId: z.string().optional(),
			partnerId: z.string().optional(),
			slug: z.string().optional(),
			isActive: z.coerce.boolean().optional(),
			planHint: z.enum(["monthly", "yearly"]).optional(),
		})
		.optional(),
});
export type AdminReferralLinkListInput = z.infer<
	typeof AdminReferralLinkListInputSchema
>;

/**
 * Admin referral link list output.
 */
export const AdminReferralLinkListOutputSchema = adminListOutput(
	AdminReferralLinkSchema,
);
export type AdminReferralLinkListOutput = z.infer<
	typeof AdminReferralLinkListOutputSchema
>;

/**
 * Admin referral link single item output.
 */
export const AdminReferralLinkOutputSchema = adminItemOutput(
	AdminReferralLinkSchema,
);
export type AdminReferralLinkOutput = z.infer<
	typeof AdminReferralLinkOutputSchema
>;

/**
 * Admin referral link create input.
 */
export const AdminReferralLinkCreateInputSchema = z.object({
	appId: z.string(),
	partnerId: z.string(),
	slug: z.string().min(1),
	payoutRateBps: z.number().int().nonnegative(),
	minPayoutCents: z.number().int().nonnegative().optional(),
	payoutDurationDays: z.number().int().positive().nullable().optional(),
	planHint: z.enum(["monthly", "yearly"]).nullable().optional(),
	maxRedemptions: z.number().int().positive().nullable().optional(),
	expiresAt: z.coerce.date().nullable().optional(),
	isActive: z.boolean().optional(),
});
export type AdminReferralLinkCreateInput = z.infer<
	typeof AdminReferralLinkCreateInputSchema
>;

/**
 * Admin referral link update input.
 */
export const AdminReferralLinkUpdateInputSchema = z.object({
	id: z.string(),
	slug: z.string().min(1).optional(),
	payoutRateBps: z.number().int().nonnegative().optional(),
	payoutDurationDays: z.number().int().positive().nullable().optional(),
	planHint: z.enum(["monthly", "yearly"]).nullable().optional(),
	maxRedemptions: z.number().int().positive().nullable().optional(),
	expiresAt: z.coerce.date().nullable().optional(),
	isActive: z.boolean().optional(),
});
export type AdminReferralLinkUpdateInput = z.infer<
	typeof AdminReferralLinkUpdateInputSchema
>;
